import { type Repub, REPUBS } from '@cx-modules/constants';

/**
 * Error-page copy is bundled at build time rather than fetched from the i18n GCS
 * bundle, so the error page renders even when that fetch is the thing that failed.
 * Values are snapshotted from the `label` namespace
 * (storage.googleapis.com/prod-template-meta/i18n/{locale}/label.json) — the same
 * `CX_ERROR_PAGE_*` keys the page used to read at runtime. EN_GB's picks/betting-guides
 * CTAs deliberately DIVERGE from that bundle, which holds US slugs (`picks-parlays`,
 * `betting-guides`) that have no UK page and 404 — do not resync them. EN_GB is also
 * FALLBACK_STRINGS, so those two values serve every repub whose locale is absent from
 * STRINGS_BY_LOCALE (AU, CA, CX, DK, FR, IN, KE, NG, NL, SE, ZA), not OC alone. The surrounding markup
 * (`ErrorContent`) appends the trailing `!` / `.` / `?` punctuation, so keep these
 * strings free of it to match the previous rendering.
 *
 * Copy edits here are a code change — they will not auto-update from the CMS.
 */
export interface ErrorPageStrings {
  bettingGuidesCtaText: string;
  bettingGuidesCtaUrl: string;
  description: string;
  freeBetsCtaText: string;
  freeBetsCtaUrl: string;
  header: string;
  internalServerError: string;
  mainCta: string;
  picksCtaText: string;
  picksCtaUrl: string;
  supportText: string;
}

const EN_GB: ErrorPageStrings = {
  internalServerError: 'Internal Server Error',
  header: 'OOPS',
  description: 'There was an error while submitting your request. Please try again later',
  mainCta: 'GO TO HOMEPAGE',
  supportText: "Why don't you check out other pages",
  freeBetsCtaText: 'FREE BETS',
  freeBetsCtaUrl: 'free-bets',
  picksCtaText: 'TIPS',
  picksCtaUrl: 'tips',
  bettingGuidesCtaText: 'INSIGHTS',
  bettingGuidesCtaUrl: 'insight',
};

// en-US / en-NZ override all three CTAs: the US estate's own hubs, which en-GB must not leak into.
const EN_US: ErrorPageStrings = {
  ...EN_GB,
  freeBetsCtaText: 'BONUS BETS',
  freeBetsCtaUrl: 'bonus-bets',
  picksCtaText: 'PICKS',
  picksCtaUrl: 'picks-parlays',
  bettingGuidesCtaText: 'BETTING GUIDES',
  bettingGuidesCtaUrl: 'betting-guides',
};

const DE_DE: ErrorPageStrings = {
  internalServerError: 'Internal Server Error',
  header: 'Ups',
  description: 'Es gab einen Fehler bei der Übermittlung Ihrer Anfrage. Bitte versuchen Sie es später erneut',
  mainCta: 'Zur Startseite gehen',
  supportText: 'Warum schauen Sie sich nicht andere Seiten an',
  freeBetsCtaText: 'Gratiswetten',
  freeBetsCtaUrl: 'free-bets',
  picksCtaText: 'Tipps',
  picksCtaUrl: 'tipps',
  bettingGuidesCtaText: 'Wettanleitungen',
  bettingGuidesCtaUrl: 'wettanleitungen',
};

// Shared by every Spanish-language repub (identical copy in the label bundle).
const ES: ErrorPageStrings = {
  internalServerError: 'Error interno del servidor',
  header: '¡Vaya',
  description: 'En este momento el sitio web no está disponible. Por favor, vuelva a intentarlo más tarde',
  mainCta: 'Ir a la página principal',
  supportText: '¿Le gustaría visitar otras páginas',
  freeBetsCtaText: 'Casas de apuestas',
  freeBetsCtaUrl: 'casas-de-apuestas',
  picksCtaText: 'Pronósticos',
  picksCtaUrl: 'pronosticos',
  bettingGuidesCtaText: 'Casinos online',
  bettingGuidesCtaUrl: 'casino',
};

// Shared by pt-PT and pt-BR (identical copy in the label bundle).
const PT: ErrorPageStrings = {
  internalServerError: 'Erro interno do servidor',
  header: 'Xiii',
  description:
    'A página que você está tentando acessar não está disponível no momento. Por favor tente novamente mais tarde',
  mainCta: 'Ir para a página inicial',
  supportText: 'Porque não dar uma olhada nas outras páginas',
  freeBetsCtaText: 'Casas de Apostas',
  freeBetsCtaUrl: 'casas-apostas',
  picksCtaText: 'Palpites',
  picksCtaUrl: 'palpites',
  bettingGuidesCtaText: 'Cassinos Online',
  bettingGuidesCtaUrl: 'cassinos-online',
};

const IT_IT: ErrorPageStrings = {
  internalServerError: 'Errore del server',
  header: 'Ops',
  description: 'Pagina non trovata. Riprova dopo',
  mainCta: 'Torna alla homepage',
  supportText: 'Ti potrebbero interessare...',
  freeBetsCtaText: 'Bonus',
  freeBetsCtaUrl: 'bonus-scommesse',
  picksCtaText: 'Pronostici',
  picksCtaUrl: 'pronostici',
  bettingGuidesCtaText: 'Notizie',
  bettingGuidesCtaUrl: 'news',
};

// it-SM (San Marino) matches it-IT except the betting-guides URL.
const IT_SM: ErrorPageStrings = {
  ...IT_IT,
  bettingGuidesCtaUrl: 'betting-guides',
};

/**
 * One entry per locale that has bundled copy. Locales whose label bundle has no
 * `CX_ERROR_PAGE_*` keys (nl-NL, fr-FR, sv-SE) and any other unlisted locale fall
 * back to en-GB — the same English the runtime fetch produced when copy was absent.
 */
const STRINGS_BY_LOCALE: Record<string, ErrorPageStrings> = {
  'en-GB': EN_GB,
  'en-US': EN_US,
  'en-NZ': EN_US,
  'de-DE': DE_DE,
  'es-ES': ES,
  'es-CO': ES,
  'es-MX': ES,
  'pt-PT': PT,
  'pt-BR': PT,
  'it-IT': IT_IT,
  'it-SM': IT_SM,
};

const FALLBACK_STRINGS = EN_GB;

export function getErrorPageStrings(repub: Repub): ErrorPageStrings {
  const locale = REPUBS[repub]?.locale;
  return (locale && STRINGS_BY_LOCALE[locale]) || FALLBACK_STRINGS;
}
